import { KioskTodayView } from './KioskTodayView.jsx';
import { KioskClockView } from './KioskClockView.jsx';
import { KioskTasksView } from './KioskTasksView.jsx';
import { KioskKnowledgeView } from './KioskKnowledgeView.jsx';
import { KioskCashCloseView } from './KioskCashCloseView.jsx';
import { KioskIncidentReport } from './KioskIncidentReport.jsx';
import { KioskInventoryView } from './KioskInventoryView.jsx';
import { KioskStockView } from './KioskStockView.jsx';
import { KioskCountView } from './KioskCountView.jsx';
import { useState, useEffect } from 'react';
import { kioskApi } from '../lib/kiosk-api.js';
import { timeclockEnabled } from '../lib/flags.js';

export function KioskShell({ session, tab, setTab, onLogout }) {
  // The 86 board tab shows only when the admin toggle is on and this person may
  // mark items out (docs/STOCK-SPEC.md §14a). A failed check just hides it.
  // The Count tab (inventory counts → Square, §7.3) shows to anyone with
  // count_stock, board toggle or not: counting is how the board's data gets fixed.
  const [stockOn, setStockOn] = useState(false);
  const [countOn, setCountOn] = useState(false);
  useEffect(() => {
    if (!session?.token) return;
    kioskApi('GET', `/api/kiosk/${session.location.key}/stock/status`, null, session.token)
      .then((s) => { setStockOn(!!(s.enabled && s.can_mark)); setCountOn(!!s.can_count); })
      .catch(() => { setStockOn(false); setCountOn(false); });
  }, [session?.token, session?.location?.key]);

  // Approved users (owner/manager/shift lead — the use_tally capability) can
  // launch Tally straight from the iPad instead of their phone. Baristas never
  // see this. Opens in a new tab (same as kiosk doc-opening) so the kiosk shell
  // stays put; the server issues a shorter (2h) Tally cookie for the shared
  // device. On the phone this button lives in the main nav; here it's inline.
  const canTally = !!(typeof window !== 'undefined'
    && window.EmpireRoles?.hasCapability?.(session.user, 'use_tally'));
  // The tab is opened inside the tap, before the handoff round-trip: iPadOS
  // silently blocks a window.open that runs after an await. At Broadway on
  // 2026-10-14 eleven taps all got a handoff URL and nothing opened. Same
  // pattern as the phone launcher in Dashboard.jsx.
  async function launchTally() {
    let w = null;
    try { w = window.open('', '_blank'); } catch { w = null; }
    try {
      const { url } = await kioskApi('POST', '/api/tally/handoff', {}, session.token);
      if (w && !w.closed) w.location = url;
      else window.location.assign(url);
    } catch (e) {
      if (w && !w.closed) w.close();
      alert('Could not open Tally: ' + (e?.message || 'unknown error'));
    }
  }

  const tabs = [
    { id: 'today',       label: 'Today',     icon: '☀' },
    // CJ 2026-07-29: kiosk clock retired — staff clock in Square now.
    ...(timeclockEnabled() ? [{ id: 'clock', label: 'Clock', icon: '⏱' }] : []),
    { id: 'tasks',       label: 'Tasks',     icon: '☑' },
    ...(stockOn ? [{ id: 'stock', label: '86', icon: '🚫' }] : []),
    ...(countOn ? [{ id: 'count', label: 'Count', icon: '🔢' }] : []),
    // CJ 2026-10-17: the kiosk is for in-store work only. Board, Equipment and
    // Maintenance live in the phone/web app, not on the counter iPad.
    { id: 'knowledge',   label: 'Knowledge', icon: '📖' },
    { id: 'close',       label: 'Close',     icon: '💰' },
    { id: 'log',         label: 'Log',       icon: '🗑' },
    { id: 'report',      label: 'Report',    icon: '🚨' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#fafafa', color: '#212121',
                   display: 'flex', flexDirection: 'column', fontFamily: 'inherit',
                   paddingTop: 'env(safe-area-inset-top, 0)' }}>
      {/* Header */}
      <div style={{ background: '#1c1f1d', color: '#fff', padding: '14px 20px',
                     display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/assets/monarch-icon-180.png" alt="" style={{ width: 32, height: 32 }} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{session.location?.name || 'Kiosk'}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>Signed in as {session.user.name}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {canTally && (
            <button onClick={launchTally}
              style={{ background: '#fff', border: 'none', color: '#1c1f1d', display: 'flex', alignItems: 'center', gap: 6,
                        padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              <img src="/assets/tally-icon.png" alt="" style={{ width: 16, height: 16 }} /> Open Tally
            </button>
          )}
          <button onClick={onLogout}
            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.25)', color: '#fff',
                      padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            Log out
          </button>
        </div>
      </div>

      {/* Tab strip */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e0e0e0', display: 'flex',
                     overflowX: 'auto', flexShrink: 0 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ flex: 1, padding: '14px 12px', fontSize: 13, fontWeight: 600,
                      background: 'transparent', border: 'none',
                      borderBottom: `3px solid ${tab === t.id ? 'var(--forest)' : 'transparent'}`,
                      color: tab === t.id ? '#212121' : '#757575',
                      cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
            <span style={{ marginRight: 6 }}>{t.icon}</span>{t.label}
          </button>
        ))}
      </div>

      {/* Body — fall back to Today if the active tab isn't in the visible set
          (e.g. a stale persisted 'clock' after the clock was retired), so the
          body never renders blank. */}
      <div style={{ flex: 1, padding: 16, overflowY: 'auto' }}>
        {(() => {
          const active = tabs.some((t) => t.id === tab) ? tab : 'today';
          switch (active) {
            case 'today':       return <KioskTodayView session={session} />;
            case 'clock':       return timeclockEnabled() ? <KioskClockView session={session} /> : <KioskTodayView session={session} />;
            case 'tasks':       return <KioskTasksView session={session} />;
            case 'stock':       return stockOn ? <KioskStockView session={session} /> : <KioskTodayView session={session} />;
            case 'count':       return <KioskCountView session={session} />;
            case 'knowledge':   return <KioskKnowledgeView session={session} />;
            case 'close':       return <KioskCashCloseView session={session} />;
            case 'log':         return <KioskInventoryView session={session} />;
            case 'report':      return <KioskIncidentReport session={session} />;
            default:            return <KioskTodayView session={session} />;
          }
        })()}
      </div>

      <div style={{ background: 'rgba(0,0,0,0.04)', padding: '8px 16px env(safe-area-inset-bottom, 8px)',
                     fontSize: 11, color: '#9e9e9e', textAlign: 'center', borderTop: '1px solid #e0e0e0' }}>
        Auto-logout after a minute of inactivity. Tap "Log out" to switch users.
      </div>
    </div>
  );
}
